import { useCallback } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  LocationResponseDto,
  ProductCreateRequestDto,
  ProductDetailsResponseDto,
} from '@tiendanube/common';
import useCanUseUnstableApiV2 from 'App/hooks/useCanUseUnstableApiV2';
import { LanguagesType } from 'App/i18n';
import { AppDispatchType, useAppDispatch } from 'App/store';
import { useGetLanguage } from 'domains/Auth/hooks';
import { cleanUpInventory } from 'domains/Catalog/Inventory/inventorySlice';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import { productPayloadBuilder } from '../../../ProductPayloadBuilder/productPayloadBuilder';
import {
  addProduct as addProductAction,
  addProductWithUnstableApi as addProductWithUnstableApiAction,
} from '../../../productsSlice';

type addProductType = (
  product: ProductCreateRequestDto,
) => Promise<ProductDetailsResponseDto>;

interface UseCreateProductResult {
  addProduct: addProductType;
}

function useCreateProduct(): UseCreateProductResult {
  const dispatch = useAppDispatch();
  const language = useGetLanguage();
  const { locations } = useLocations();

  const canUseUnstableApiV2 = useCanUseUnstableApiV2();

  const addProduct: addProductType = useCallback(
    async (product) => {
      if (canUseUnstableApiV2) {
        return applyApiV2(dispatch, product, language, locations);
      } else {
        return applyApiV1(dispatch, product);
      }
    },
    [dispatch, canUseUnstableApiV2, language, locations],
  );

  return { addProduct };
}

async function applyApiV2(
  dispatch: AppDispatchType,
  product: ProductCreateRequestDto,
  language: LanguagesType,
  locations: LocationResponseDto[],
): Promise<ProductDetailsResponseDto> {
  const payload = productPayloadBuilder.create(product, language, locations);
  const result = await dispatch(
    addProductWithUnstableApiAction({ product: payload }),
  );
  const productDetailsResponse = unwrapResult(result);

  dispatch(cleanUpInventory());
  return productDetailsResponse;
}

async function applyApiV1(
  dispatch: AppDispatchType,
  product: ProductCreateRequestDto,
): Promise<ProductDetailsResponseDto> {
  const result = await dispatch(addProductAction({ product }));
  const productResponse = unwrapResult(result);
  dispatch(cleanUpInventory());
  return productResponse;
}

export default useCreateProduct;
